import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

/** A registered data source a catalog-backed select can bind to. */
export interface OptionSource {
  key: string;
  label: string;
}

/** A resolved {value,label} option from a data source. */
export interface OptionSourceOption {
  value: string;
  label: string;
}

export const optionSourceKeys = {
  all: ['option-sources'] as const,
  sources: () => [...optionSourceKeys.all, 'list'] as const,
  options: (key: string) => [...optionSourceKeys.all, 'options', key] as const,
};

/**
 * List the data sources available to the current tenant for catalog-backed
 * selects (e.g. "manufacturer"). Used by the schema builder's field panel.
 */
export function useOptionSources() {
  return useQuery({
    queryKey: optionSourceKeys.sources(),
    queryFn: async () => {
      const res = await api.get<ApiResponse<OptionSource[]>>('/api/metadata/option-sources');
      return res.data.data;
    },
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Resolve a data source key to its current {value,label} options. Used at form
 * render time by catalog-backed selects. Disabled (no fetch) when `key` is
 * falsy or `enabled` is false.
 */
export function useOptionSourceOptions(key: string | undefined, enabled = true) {
  return useQuery({
    queryKey: optionSourceKeys.options(key ?? ''),
    queryFn: async () => {
      const res = await api.get<ApiResponse<OptionSourceOption[]>>(
        `/api/metadata/option-sources/${encodeURIComponent(key as string)}/options`
      );
      return res.data.data;
    },
    enabled: enabled && !!key,
    staleTime: 1000 * 60 * 5,
  });
}
